/**
 * Duel SFX. Prefers Pixabay clips in /sfx, else SoftSynth.
 * No Konami samples.
 */

import { Bgm } from './Bgm'
import { SampleBank } from './SampleBank'
import { synth } from './SoftSynth'

export type SfxKind =
  | 'draw'
  | 'summon'
  | 'set'
  | 'flip'
  | 'spell'
  | 'trap'
  | 'attack'
  | 'impact'
  | 'damage'
  | 'heal'
  | 'destroy'
  | 'banish'
  | 'toHand'
  | 'stat'
  | 'phase'
  | 'turn'
  | 'win'
  | 'lose'
  | 'click'
  | 'dice'
  | 'packWhoosh'
  | 'packTear'
  | 'packRip'
  | 'packBurst'
  | 'packSlide'
  | 'packCommon'
  | 'packRare'
  | 'packSuper'
  | 'packUltra'
  | 'packSecret'
  | 'packSummary'
  | 'packSkip'

const MUTE_KEY = 'yugioh.sfxMuted'
const VOLUME_KEY = 'yugioh.sfxVolume'
const DEFAULT_VOLUME = 1
const SAMPLE_GAIN = 0.82

/** Per-kind multiplier on SAMPLE_GAIN. LP cues sit under attack/impact. */
const KIND_GAIN: Partial<Record<SfxKind, number>> = {
  damage: 2.15,
  heal: 2.0,
}

const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : DEFAULT_VOLUME))

const readVolume = (): number => {
  try {
    const raw = localStorage.getItem(VOLUME_KEY)
    if (raw === null) return DEFAULT_VOLUME
    return clamp01(Number(raw))
  } catch {
    return DEFAULT_VOLUME
  }
}

const DUEL_SAMPLES: Record<SfxKind, string> = {
  draw: 'duel-draw',
  summon: 'duel-summon',
  set: 'duel-set',
  flip: 'duel-flip',
  spell: 'duel-spell',
  trap: 'duel-trap',
  attack: 'duel-attack',
  impact: 'duel-impact',
  damage: 'duel-damage',
  heal: 'duel-heal',
  destroy: 'duel-destroy',
  banish: 'duel-banish',
  toHand: 'duel-hand',
  stat: 'duel-stat',
  phase: 'duel-phase',
  turn: 'duel-turn',
  win: 'duel-win',
  lose: 'duel-lose',
  click: 'duel-click',
  dice: 'duel-dice',
  packWhoosh: 'pack-whoosh',
  packTear: 'pack-tear',
  packRip: 'pack-rip',
  packBurst: 'pack-burst',
  packSlide: 'pack-slide',
  packCommon: 'pack-common',
  packRare: 'pack-rare',
  packSuper: 'pack-super',
  packUltra: 'pack-ultra',
  packSecret: 'pack-secret',
  packSummary: 'pack-summary',
  packSkip: 'pack-skip',
}

class DuelSfxController {
  private muted = false
  private volume = readVolume()
  private keepAlive: OscillatorNode | null = null
  private lastSpellTrapAt = 0

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTE_KEY) === '1'
    } catch {
      this.muted = false
    }
    SampleBank.preload(Object.values(DUEL_SAMPLES))
    this.syncOutput()
    const wake = () => this.prime()
    window.addEventListener('pointerdown', wake)
    window.addEventListener('keydown', wake)
  }

  isMuted(): boolean {
    return this.muted
  }

  /**
   * Linear gain 0–1. Persists in this browser. Sound off still silences
   * everything until it is turned back on.
   */
  getVolume(): number {
    return this.volume
  }

  /**
   * Sets linear gain 0–1 and writes it to localStorage.
   */
  setVolume(value: number): void {
    this.volume = clamp01(value)
    try {
      localStorage.setItem(VOLUME_KEY, String(this.volume))
    } catch {
      /* ignore */
    }
    this.syncOutput()
  }

  setMuted(value: boolean): void {
    this.muted = value
    try {
      localStorage.setItem(MUTE_KEY, value ? '1' : '0')
    } catch {
      /* ignore */
    }
    this.syncOutput()
  }

  toggle(): boolean {
    this.setMuted(!this.muted)
    return this.muted
  }

  unlock(): void {
    const ctx = synth.unlock()
    Bgm.unlock()
    if (ctx) SampleBank.attach(ctx)
  }

  prime(): void {
    Bgm.unlock()
    const ctx = synth.unlock()
    if (!ctx) return
    SampleBank.attach(ctx)
    synth.setUserGain(this.muted ? 0 : this.volume)
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    g.gain.value = 0.00001
    osc.connect(g)
    g.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.02)
  }

  hold(): void {
    this.prime()
    const ctx = synth.unlock()
    if (!ctx || this.keepAlive) return
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    g.gain.value = 0.00001
    osc.connect(g)
    g.connect(ctx.destination)
    osc.start()
    this.keepAlive = osc
  }

  release(): void {
    try {
      this.keepAlive?.stop()
    } catch {
      /* already stopped */
    }
    this.keepAlive = null
  }

  /**
   * Plays a cue. `gainMul` scales this shot only (menu clicks use ~0.55).
   */
  play(kind: SfxKind, gainMul = 1): void {
    if (kind === 'spell' || kind === 'trap') {
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now()
      if (now - this.lastSpellTrapAt < 280) return
      this.lastSpellTrapAt = now
    }
    const gain = this.sampleGain(kind) * Math.max(0, gainMul)
    if (gain <= 0) return
    if (SampleBank.play(DUEL_SAMPLES[kind], gain)) return
    const ctx = synth.unlock()
    if (!ctx) return
    const start = () => {
      if (this.sampleGain(kind) <= 0) return
      this.emit(kind, synth.now())
    }
    if (ctx.state === 'suspended') {
      void ctx.resume().then(start)
      return
    }
    start()
  }

  private emit(kind: SfxKind, t: number): void {
    switch (kind) {
      case 'draw':
        synth.laser(1500, 520, t, 0.14, 0.14)
        synth.tone(90, t + 0.04, 0.08, 0.1, { type: 'square', attack: 0.004 })
        break
      case 'summon':
        synth.thud(t, 0.28)
        synth.laser(80, 520, t, 0.26, 0.16)
        synth.tone(1240, t + 0.12, 0.1, 0.1, { type: 'triangle', reverb: 0.2 })
        break
      case 'set':
        synth.tone(70, t, 0.1, 0.18, { type: 'square', attack: 0.003 })
        synth.thud(t, 0.16)
        break
      case 'flip':
        synth.laser(1900, 700, t, 0.1, 0.14)
        synth.tone(1560, t + 0.05, 0.06, 0.1, { type: 'square', attack: 0.003 })
        break
      case 'spell':
        synth.laser(280, 1100, t, 0.22, 0.14)
        synth.tone(330, t, 0.2, 0.08, { type: 'sawtooth', reverb: 0.22 })
        synth.shimmer(t + 0.06, 0.16, 0.14)
        break
      case 'trap':
        synth.tone(170, t, 0.08, 0.16, { type: 'square', attack: 0.003 })
        synth.tone(85, t + 0.09, 0.1, 0.14, { type: 'square', attack: 0.003 })
        synth.noise(t, 0.1, 0.16, {
          color: 'white',
          filter: 'bandpass',
          freq: 900,
          q: 2.2,
        })
        break
      case 'attack':
        synth.laser(2200, 160, t, 0.2, 0.22)
        synth.tone(55, t + 0.04, 0.12, 0.16, { type: 'sine' })
        break
      case 'impact':
        synth.thud(t, 0.42)
        synth.noise(t, 0.1, 0.22, {
          color: 'white',
          filter: 'lowpass',
          freq: 500,
          sweepTo: 140,
        })
        break
      case 'damage':
        synth.tone(62, t, 0.22, 0.36, { type: 'sawtooth' })
        synth.thud(t, 0.4)
        synth.noise(t, 0.12, 0.18, {
          color: 'white',
          filter: 'lowpass',
          freq: 420,
          sweepTo: 90,
        })
        break
      case 'heal':
        synth.tone(392, t, 0.1, 0.16, { type: 'triangle' })
        synth.tone(523, t + 0.06, 0.12, 0.16, { type: 'triangle' })
        synth.tone(659, t + 0.12, 0.16, 0.16, { type: 'sine', reverb: 0.18 })
        break
      case 'destroy':
        synth.laser(700, 40, t, 0.24, 0.18)
        synth.noise(t, 0.12, 0.2, {
          color: 'white',
          filter: 'highpass',
          freq: 1800,
        })
        break
      case 'banish':
        synth.laser(180, 2400, t, 0.28, 0.16)
        synth.tone(90, t, 0.2, 0.1, { type: 'triangle', reverb: 0.25 })
        break
      case 'toHand':
        synth.sweep(360, 820, t, 0.14, 0.12, 0.1, 'triangle')
        synth.tone(100, t + 0.03, 0.07, 0.08, { type: 'square' })
        break
      case 'stat':
        synth.tone(1080, t, 0.05, 0.1, { type: 'square', attack: 0.003 })
        synth.tone(1440, t + 0.06, 0.05, 0.09, { type: 'square', attack: 0.003 })
        break
      case 'phase':
        synth.tone(260, t, 0.06, 0.08, { type: 'triangle' })
        break
      case 'turn':
        synth.laser(200, 640, t, 0.18, 0.12)
        synth.tone(80, t + 0.05, 0.1, 0.1, { type: 'sine' })
        break
      case 'win':
        synth.thud(t, 0.2)
        synth.tone(320, t + 0.04, 0.1, 0.12, { type: 'square' })
        synth.tone(480, t + 0.14, 0.1, 0.12, { type: 'square' })
        synth.tone(640, t + 0.24, 0.14, 0.12, { type: 'triangle', reverb: 0.2 })
        synth.laser(400, 1200, t + 0.08, 0.28, 0.1)
        break
      case 'lose':
        synth.laser(240, 50, t, 0.32, 0.16)
        synth.tone(70, t + 0.1, 0.24, 0.12, { type: 'sawtooth' })
        break
      case 'click':
        synth.tone(2100, t, 0.03, 0.1, { type: 'square', attack: 0.002 })
        break
      case 'dice':
        synth.thud(t, 0.18)
        synth.tone(140, t, 0.08, 0.1, { type: 'square', attack: 0.003 })
        synth.tone(90, t + 0.08, 0.1, 0.12, { type: 'square' })
        synth.thud(t + 0.22, 0.22)
        break
      case 'packWhoosh':
        synth.laser(2000, 300, t, 0.18, 0.16)
        break
      case 'packTear':
        synth.tone(1800, t, 0.04, 0.12, { type: 'square', attack: 0.002 })
        synth.tone(2300, t + 0.03, 0.04, 0.1, { type: 'square', attack: 0.002 })
        break
      case 'packRip':
        synth.laser(1500, 80, t, 0.24, 0.18)
        synth.thud(t + 0.05, 0.16)
        break
      case 'packBurst':
        synth.thud(t, 0.2)
        synth.laser(160, 1300, t, 0.18, 0.14)
        synth.tone(1600, t + 0.1, 0.08, 0.1, { type: 'triangle' })
        break
      case 'packSlide':
        synth.sweep(300, 640, t, 0.12, 0.12, 0.08, 'triangle')
        break
      case 'packCommon':
        synth.tone(720, t, 0.06, 0.12, { type: 'square', attack: 0.003 })
        synth.tone(480, t + 0.07, 0.07, 0.1, { type: 'square', attack: 0.003 })
        break
      case 'packRare':
        synth.laser(240, 980, t, 0.2, 0.12)
        synth.tone(1480, t + 0.12, 0.1, 0.1, { type: 'triangle' })
        break
      case 'packSuper':
        synth.laser(140, 880, t, 0.26, 0.14)
        synth.tone(660, t + 0.16, 0.1, 0.12, { type: 'square' })
        break
      case 'packUltra':
        synth.thud(t, 0.26)
        synth.laser(90, 760, t + 0.04, 0.32, 0.14)
        synth.tone(1980, t + 0.22, 0.12, 0.1, { type: 'triangle' })
        break
      case 'packSecret':
        synth.tone(64, t, 0.4, 0.16, { type: 'sine', reverb: 0.25 })
        synth.laser(700, 1700, t + 0.08, 0.32, 0.1)
        synth.tone(1400, t + 0.28, 0.1, 0.08, { type: 'square' })
        break
      case 'packSummary':
        synth.tone(840, t, 0.07, 0.1, { type: 'square' })
        synth.tone(560, t + 0.09, 0.08, 0.1, { type: 'square' })
        synth.tone(360, t + 0.18, 0.1, 0.1, { type: 'square' })
        break
      case 'packSkip':
        synth.tone(1900, t, 0.04, 0.1, { type: 'square', attack: 0.002 })
        break
    }
  }

  private sampleGain(kind: SfxKind): number {
    if (this.muted) return 0
    return SAMPLE_GAIN * (KIND_GAIN[kind] ?? 1) * this.volume
  }

  private syncOutput(): void {
    Bgm.setSfxMuted(this.muted)
    synth.setUserGain(this.muted ? 0 : this.volume)
  }
}

export const DuelSfx = new DuelSfxController()
